import { widgetApi } from "../services/api";
import type { CanonicalName } from "./parse";

/**
 * Recipient book — `GET /widget/links/:linkId/recipients`.
 *
 * Derived read model over the user's operations (aggregated by
 * `recipientFingerprint`). Failures resolve to `[]`.
 */

export interface RecipientDestinationCash {
  stateCode?: string;
  payerCode?: string;
}

export interface RecipientDestinationDisplay {
  bankName?: string;
  maskedAccount?: string;
  cardBrand?: string;
  cardLast4?: string;
}

export interface RecipientDestination {
  payoutMethod: string;
  cash?: RecipientDestinationCash;
  paymentMethodId?: string;
  display?: RecipientDestinationDisplay;
  phone?: string;
  lastCapabilityKey?: string;
  lastUsedAt: string;
  timesUsed: number;
}

export interface RecipientBookEntry {
  recipientKey: string;
  name: CanonicalName;
  address?: {
    countryISO2?: string;
    state?: string;
    city?: string;
    street?: string;
    postalCode?: string;
  };
  phone?: string;
  lastUsedAt: string;
  timesUsed: number;
  destinations: RecipientDestination[];
}

interface ListRecipientsApiResponse {
  success: boolean;
  data: RecipientBookEntry[];
}

export const listRecipients = async (
  linkId: string,
  limit = 10
): Promise<RecipientBookEntry[]> => {
  try {
    const { data } = await widgetApi.get<ListRecipientsApiResponse>(
      `/widget/links/${linkId}/recipients`,
      { params: { limit } }
    );
    return data.data ?? [];
  } catch {
    return [];
  }
};

/** Joins a `CanonicalName` into the display string (LatAm order). */
export const formatCanonicalName = (name: CanonicalName): string =>
  [name.firstName, name.middleName, name.lastName, name.secondLastName]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(" ");
